import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, Users, Clock, CheckCircle, Calendar, IndianRupee,
  UserCheck, LayoutDashboard, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import {
  getDoctors, getQueueToday, getAppointmentsByDateRange, getPatient,
  queueCheckin, queueWalkin, queueCancel, queueReorder, updateAppointment,
  getBillingServices, createBillingInvoice,
  recordInvoicePayment,
  type QueueEntry, type QueueTodayResponse,
} from '@/lib/api';

import QueueAddSearch from '@/components/FrontDesk/QueueAddSearch';
import PatientContextPanel from '@/components/FrontDesk/PatientContextPanel';
import PatientDrawer from '@/components/FrontDesk/PatientDrawer';
import MedicalBillingModal from '@/components/Patient/MedicalBillingModal';
import { PaymentRecordModal } from '@/components/Billing/BillingModals';
import PatientFormModal from '@/components/Patient/PatientFormModal';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';
import PrintPrescriptionPreviewModal from '@/components/Prescription/PrintPrescriptionPreviewModal';
import PrintBillPreviewModal from '@/components/Billing/PrintBillPreviewModal';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Doctor { id: string; name: string }

interface Appointment {
  id: string;
  patient_id: string;
  date: string;
  time: string;
  reason: string;
  status: string;
  doctor_id?: string;
}

interface AppointmentRow extends Appointment {
  patientName: string;
  patientPhone: string;
  patientAge?: number;
  patientGender?: string;
  patientMrn?: string;
}

type QueueEntryEx = QueueEntry & { doctorName: string };

type SelectedItem =
  | { kind: 'queue'; entry: QueueEntryEx }
  | { kind: 'appointment'; apt: AppointmentRow };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function drName(name: string) {
  return name.startsWith('Dr.') ? name : `Dr. ${name}`;
}

function formatTime(time: string) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function waitColor(mins: number | null | undefined) {
  if (!mins) return '';
  if (mins >= 30) return 'text-destructive';
  if (mins >= 15) return 'text-warning';
  return 'text-muted-foreground';
}

// ─── Live clock sub-component ─────────────────────────────────────────────────

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  const dateStr = now.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  return (
    <div className="flex-shrink-0 select-none">
      <p className="text-lg font-bold tabular-nums leading-none tracking-tight text-foreground">
        {timeStr}
      </p>
      <p className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wide">{dateStr}</p>
    </div>
  );
}

// ─── Stat chip sub-component ──────────────────────────────────────────────────

function StatChip({ label, value, colorClass, pulse = false }: {
  label: string; value: number; colorClass: string; pulse?: boolean;
}) {
  return (
    <div className="text-center flex-shrink-0">
      <div className="flex items-center justify-center gap-1">
        {pulse && value > 0 && (
          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
        )}
        <p className={`text-base font-bold tabular-nums leading-none ${colorClass}`}>{value}</p>
      </div>
      <p className="text-[10px] text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function FrontDeskCommandCenter() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { can } = usePermission();
  const clinicId = currentUser?.clinic_id ?? '';

  // ── Core data ──────────────────────────────────────────────────────────────
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>('all');
  const [queues, setQueues] = useState<Record<string, QueueTodayResponse>>({});
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [aptPatients, setAptPatients] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // ── Selection ──────────────────────────────────────────────────────────────
  const [selected, setSelected] = useState<SelectedItem | null>(null);

  // ── Modals ─────────────────────────────────────────────────────────────────
  const [billingPatient, setBillingPatient] = useState<any>(null);
  const [billingServices, setBillingServices] = useState<any[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [collectInvoice, setCollectInvoice] = useState<any>(null);
  const [editPatient, setEditPatient] = useState<any>(null);
  const [bookPatient, setBookPatient] = useState<any>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [prefillPhone, setPrefillPhone] = useState('');
  const [printRx, setPrintRx] = useState<any>(null);
  const [printInvoice, setPrintInvoice] = useState<any>(null);
  const [drawerPatientId, setDrawerPatientId] = useState<string | null>(null);
  const [checkingInId, setCheckingInId] = useState<string | null>(null);
  const [queueActionId, setQueueActionId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchQueues = useCallback(async (docList: Doctor[]) => {
    if (!clinicId || docList.length === 0) return;
    const results = await Promise.allSettled(
      docList.map(d => getQueueToday(d.id, clinicId))
    );
    const next: Record<string, QueueTodayResponse> = {};
    results.forEach((r, i) => {
      if (r.status === 'fulfilled' && r.value) next[docList[i].id] = r.value;
    });
    setQueues(next);
  }, [clinicId]);

  const fetchAppointmentPatients = useCallback(async (apts: Appointment[]) => {
    const ids = [...new Set(apts.map(a => a.patient_id))];
    const results = await Promise.allSettled(ids.map(id => getPatient(id)));
    const map: Record<string, any> = {};
    results.forEach((r, i) => {
      if (r.status === 'fulfilled' && r.value) map[ids[i]] = r.value;
    });
    setAptPatients(map);
  }, []);

  const loadAll = useCallback(async () => {
    if (!clinicId) return;
    try {
      const today = new Date().toISOString().split('T')[0];
      const [docList, apts] = await Promise.all([
        getDoctors(),
        getAppointmentsByDateRange(today, today, clinicId),
      ]);

      const docs: Doctor[] = docList ?? [];
      const aptsArr: Appointment[] = apts ?? [];

      setDoctors(docs);
      setAppointments(aptsArr);
      setSelectedDoctorId(prev => prev);

      await Promise.all([
        fetchQueues(docs),
        fetchAppointmentPatients(aptsArr),
      ]);
    } catch {
      toast({ title: 'Error', description: 'Failed to load front desk data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [clinicId, fetchQueues, fetchAppointmentPatients]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    pollingRef.current = setInterval(() => {
      fetchQueues(doctors);
    }, 30000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [doctors, fetchQueues]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
    toast({ title: 'Refreshed' });
  };

  // ── Derived data ───────────────────────────────────────────────────────────

  const activeDoctors = useMemo(() =>
    selectedDoctorId === 'all' ? doctors : doctors.filter(d => d.id === selectedDoctorId),
  [doctors, selectedDoctorId]);

  const allEntries = useMemo((): QueueEntryEx[] => {
    const entries: QueueEntryEx[] = [];
    activeDoctors.forEach(doc => {
      const q = queues[doc.id];
      if (!q) return;
      q.entries.forEach(e => entries.push({ ...e, doctorName: doc.name }));
    });
    return entries;
  }, [queues, activeDoctors]);

  const inConsultation = useMemo(() =>
    allEntries.filter(e => e.status === 'in_consultation'),
  [allEntries]);

  const waiting = useMemo(() =>
    allEntries.filter(e => e.status === 'waiting').sort((a, b) => a.sortOrder - b.sortOrder),
  [allEntries]);

  const completed = useMemo(() =>
    allEntries.filter(e => e.status === 'completed').sort((a, b) =>
      (b.consultationEndAt ?? '').localeCompare(a.consultationEndAt ?? '')),
  [allEntries]);

  const upcoming: AppointmentRow[] = useMemo(() => {
    const filtered = appointments.filter(a => {
      if (a.status !== 'scheduled') return false;
      if (selectedDoctorId !== 'all' && a.doctor_id && a.doctor_id !== selectedDoctorId) return false;
      return true;
    });
    return filtered
      .map(a => {
        const p = aptPatients[a.patient_id];
        return {
          ...a,
          patientName: p?.name ?? 'Unknown',
          patientPhone: p?.phone ?? '',
          patientAge: p?.age,
          patientGender: p?.gender,
          patientMrn: p?.mrn,
        };
      })
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [appointments, aptPatients, selectedDoctorId]);

  const stats = useMemo(() => {
    const scheduledToday = appointments.filter(a => {
      if (selectedDoctorId !== 'all' && a.doctor_id && a.doctor_id !== selectedDoctorId) return false;
      return true;
    }).length;
    return {
      scheduled: scheduledToday,
      waiting: waiting.length,
      inConsult: inConsultation.length,
      completed: completed.length,
      unpaid: completed.filter(() => false).length,
    };
  }, [appointments, waiting, inConsultation, completed, selectedDoctorId]);

  // ── Selected patient info ──────────────────────────────────────────────────

  const selectedPatientId = selected
    ? selected.kind === 'queue'
      ? (selected.entry.patientId ?? null)
      : selected.apt.patient_id
    : null;

  const selectedPatientName = selected
    ? selected.kind === 'queue'
      ? selected.entry.patientName
      : selected.apt.patientName
    : '';

  const selectedQueueEntry = useMemo((): QueueEntryEx | null => {
    if (!selectedPatientId) return null;
    return allEntries.find(e => e.patientId === selectedPatientId && e.status !== 'cancelled') ?? null;
  }, [allEntries, selectedPatientId]);

  // ── Queue actions ──────────────────────────────────────────────────────────

  const handleCheckIn = async (apt: AppointmentRow) => {
    setCheckingInId(apt.id);
    try {
      const entry = await queueCheckin(apt.id);
      toast({ title: 'Checked in', description: entry ? `Token #${entry.tokenNumber} · ${apt.patientName}` : apt.patientName });
      await loadAll();
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message ?? 'Check-in failed', variant: 'destructive' });
    } finally {
      setCheckingInId(null);
    }
  };

  const handlePriority = async (entry: QueueEntry) => {
    setQueueActionId(entry.id);
    try {
      await queueReorder(entry.id, true);
      await fetchQueues(doctors);
      toast({ title: 'Moved to front', description: `${entry.patientName} is now priority` });
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message ?? 'Failed', variant: 'destructive' });
    } finally {
      setQueueActionId(null);
    }
  };

  const handleRemoveFromQueue = async (entry: QueueEntry) => {
    setConfirmRemoveId(null);
    setQueueActionId(entry.id);
    try {
      await queueCancel(entry.id);
      if (entry.appointmentId) {
        await updateAppointment({ id: entry.appointmentId, status: 'scheduled' }).catch(() => {});
      }
      if (selected?.kind === 'queue' && selected.entry.id === entry.id) setSelected(null);
      await loadAll();
      toast({ title: 'Removed', description: `${entry.patientName} removed from queue` });
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message ?? 'Failed', variant: 'destructive' });
    } finally {
      setQueueActionId(null);
    }
  };

  // ── Billing helpers ────────────────────────────────────────────────────────

  const openNewInvoice = async (patient: any) => {
    setBillingPatient(patient);
    setBillingLoading(true);
    try {
      const svcs = await getBillingServices(clinicId, currentUser?.doctor_id);
      setBillingServices(Array.isArray(svcs) ? svcs : (svcs as any)?.services ?? []);
    } catch {
      setBillingServices([]);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleCreateInvoice = async (invoiceData: any) => {
    if (!billingPatient) return;
    try {
      const effectiveDoctorId = selectedDoctorId !== 'all' ? selectedDoctorId : doctors[0]?.id ?? currentUser?.doctor_id ?? '';
      await createBillingInvoice({
        patient_id:      billingPatient.id,
        doctor_id:       invoiceData.doctorId || effectiveDoctorId,
        clinic_id:       clinicId,
        invoice_date:    invoiceData.invoiceDate || undefined,
        items:           invoiceData.items,
        discount_amount: invoiceData.discount    || 0,
        discount_type:   invoiceData.discount > 0 ? invoiceData.discountType : undefined,
        tax_amount:      invoiceData.taxAmount   || 0,
        notes:           invoiceData.notes       || undefined,
        payments:                     invoiceData.payments    || [],
        balance_forward:              invoiceData.balance_forward              || 0,
        balance_forward_settle:       invoiceData.balance_forward_settle       || 0,
        balance_forward_payment_mode: invoiceData.balance_forward_payment_mode || undefined,
        deposit_amount:               invoiceData.depositAmount      || undefined,
        deposit_payment_mode:         invoiceData.depositPaymentMode || undefined,
      });
      toast({ title: 'Invoice created', description: `Bill created for ${billingPatient.name}` });
      setBillingPatient(null);
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message ?? 'Failed', variant: 'destructive' });
    }
  };

  const handleCollectPayment = async (amount: number, method: string, meta?: { reference_number?: string; upi_app?: string; bank_name?: string }) => {
    if (!collectInvoice) return;
    const modeMap: Record<string, string> = {
      cash: 'cash', upi: 'upi', card: 'card', cheque: 'cheque',
      netbanking: 'netbanking', 'bank transfer': 'netbanking',
      insurance: 'other', other: 'other',
    };
    try {
      await recordInvoicePayment(collectInvoice.id, {
        amount,
        payment_mode: (modeMap[method?.toLowerCase()] ?? 'cash') as any,
        payment_date: new Date().toISOString(),
        reference_number: meta?.reference_number || undefined,
        upi_app: meta?.upi_app || undefined,
        bank_name: meta?.bank_name || undefined,
      });
      toast({ title: 'Payment recorded', description: `₹${amount} collected` });
      setCollectInvoice(null);
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message ?? 'Failed', variant: 'destructive' });
    }
  };

  // ── Register new patient handler ───────────────────────────────────────────

  const handleRegisterNew = (phone?: string) => {
    setPrefillPhone(phone ?? '');
    setShowRegisterModal(true);
  };

  const handleRegisterSuccess = async (newPatient: any) => {
    setShowRegisterModal(false);
    const effectiveDoctorId = selectedDoctorId !== 'all' ? selectedDoctorId : doctors[0]?.id;
    if (!effectiveDoctorId) return;
    try {
      await queueWalkin(newPatient.id, effectiveDoctorId, clinicId, false);
      toast({ title: 'Registered & added to queue', description: newPatient.name });
      await loadAll();
    } catch {
      toast({ title: 'Registered', description: `${newPatient.name} registered. Add them to queue manually.` });
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const isSelectedEntry = (id: string) =>
    selected?.kind === 'queue' && selected.entry.id === id;

  const isSelectedApt = (id: string) =>
    selected?.kind === 'appointment' && selected.apt.id === id;

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-var(--topbar-height,60px))]">
        <div className="text-center">
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading front desk…</p>
        </div>
      </div>
    );
  }

  const effectiveDoctorId = selectedDoctorId !== 'all' ? selectedDoctorId : null;

  return (
    <div className="h-[calc(100vh-var(--topbar-height,60px))] flex flex-col overflow-hidden">

      {/* ── Command bar ─────────────────────────────────────────────────────── */}
      <div className="flex-none border-b border-border bg-background">

        {/* Row 1: clock + stats + controls */}
        <div className="flex items-center gap-5 px-4 py-2.5">

          {/* Live operational clock */}
          <LiveClock />

          <div className="w-px h-8 bg-border flex-shrink-0" />

          {/* KPI stats */}
          <div className="flex items-center gap-5">
            <StatChip label="Scheduled"  value={stats.scheduled}  colorClass="text-foreground" />
            <StatChip label="Waiting"    value={stats.waiting}    colorClass="text-warning" />
            <StatChip label="In Consult" value={stats.inConsult}  colorClass="text-primary" pulse />
            <StatChip label="Done"       value={stats.completed}  colorClass="text-success" />
          </div>

          {/* Controls */}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <div className="w-px h-5 bg-border" />
            <button
              onClick={() => navigate('/frontdesk/reports')}
              className="text-xs font-medium text-primary hover:underline px-1 py-1"
            >
              Reports →
            </button>
            <button
              onClick={() => window.open('/token-display', '_blank')}
              className="text-xs font-medium text-muted-foreground hover:text-foreground px-1 py-1"
              title="Open token display screen (for TV/monitor)"
            >
              Display ↗
            </button>
          </div>
        </div>

        {/* Row 2: Doctor tabs */}
        <div className="flex items-center gap-1 px-4 pb-2 overflow-x-auto">
          <button
            onClick={() => setSelectedDoctorId('all')}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
              selectedDoctorId === 'all'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            All Doctors
          </button>
          {doctors.map(d => {
            const q = queues[d.id];
            const waitCount = q?.entries.filter(e => e.status === 'waiting').length ?? 0;
            const isActive = selectedDoctorId === d.id;
            return (
              <button
                key={d.id}
                onClick={() => setSelectedDoctorId(d.id)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {drName(d.name)}
                {waitCount > 0 && (
                  <span className={`w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center flex-shrink-0 ${
                    isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-warning/20 text-warning'
                  }`}>
                    {waitCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Two-panel body ──────────────────────────────────────────────────── */}
      <div className="flex-1 flex min-h-0 overflow-hidden">

        {/* ── Left: Queue list ─────────────────────────────────────────────── */}
        <div className="w-72 flex-none flex flex-col border-r border-border bg-muted/20 overflow-hidden">

          {/* Search / add */}
          <div className="flex-none border-b border-border/60">
            <QueueAddSearch
              doctors={doctors}
              effectiveDoctorId={effectiveDoctorId}
              clinicId={clinicId}
              onAdded={() => fetchQueues(doctors)}
              onRegisterNew={handleRegisterNew}
            />
          </div>

          {/* Scrollable list */}
          <div className="flex-1 overflow-y-auto">

            {/* In Consultation */}
            {inConsultation.length > 0 && (
              <>
                <SectionHeader
                  label="In Consultation"
                  dotClass="bg-primary animate-pulse"
                  bgClass="bg-primary/5"
                />
                {inConsultation.map(entry => (
                  <QueueRow
                    key={entry.id}
                    entry={entry}
                    selected={isSelectedEntry(entry.id)}
                    showDoctor={selectedDoctorId === 'all'}
                    onClick={() => setSelected({ kind: 'queue', entry })}
                    confirmingRemove={false}
                    onRequestRemove={() => {}}
                    onConfirmRemove={() => {}}
                    onCancelRemove={() => {}}
                  />
                ))}
              </>
            )}

            {/* Waiting */}
            {waiting.length > 0 && (
              <>
                <SectionHeader
                  label={`Waiting (${waiting.length})`}
                  dotClass="bg-warning"
                  bgClass="bg-warning/5"
                />
                {waiting.map(entry => (
                  <QueueRow
                    key={entry.id}
                    entry={entry}
                    selected={isSelectedEntry(entry.id)}
                    showDoctor={selectedDoctorId === 'all'}
                    onClick={() => setSelected({ kind: 'queue', entry })}
                    confirmingRemove={confirmRemoveId === entry.id}
                    onRequestRemove={() => setConfirmRemoveId(entry.id)}
                    onConfirmRemove={() => handleRemoveFromQueue(entry)}
                    onCancelRemove={() => setConfirmRemoveId(null)}
                  />
                ))}
              </>
            )}

            {/* Upcoming appointments */}
            {upcoming.length > 0 && (
              <>
                <SectionHeader
                  label={`Schedule (${upcoming.length})`}
                  dotClass="bg-muted-foreground/40"
                  bgClass="bg-background"
                />
                {upcoming.map(apt => (
                  <ApptRow
                    key={apt.id}
                    apt={apt}
                    selected={isSelectedApt(apt.id)}
                    checkingIn={checkingInId === apt.id}
                    showDoctor={selectedDoctorId === 'all'}
                    doctors={doctors}
                    onSelect={() => setSelected({ kind: 'appointment', apt })}
                    onCheckIn={() => handleCheckIn(apt)}
                  />
                ))}
              </>
            )}

            {/* Completed */}
            {completed.length > 0 && (
              <>
                <SectionHeader
                  label={`Done (${completed.length})`}
                  dotClass="bg-success"
                  bgClass="bg-success/5"
                />
                {completed.map(entry => (
                  <QueueRow
                    key={entry.id}
                    entry={entry}
                    selected={isSelectedEntry(entry.id)}
                    showDoctor={selectedDoctorId === 'all'}
                    onClick={() => setSelected({ kind: 'queue', entry })}
                    confirmingRemove={false}
                    onRequestRemove={() => {}}
                    onConfirmRemove={() => {}}
                    onCancelRemove={() => {}}
                    dim
                  />
                ))}
              </>
            )}

            {/* Empty state */}
            {inConsultation.length === 0 && waiting.length === 0 && upcoming.length === 0 && completed.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                <LayoutDashboard className="w-8 h-8 text-muted-foreground/20 mb-3" />
                <p className="text-sm font-semibold text-muted-foreground">Queue is empty</p>
                <p className="text-xs text-muted-foreground/50 mt-1 leading-relaxed">
                  Search for a patient above<br />to add them to the queue
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ── Right: Patient context panel ─────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {selectedPatientId ? (
            <PatientContextPanel
              key={selectedPatientId}
              patientId={selectedPatientId}
              patientName={selectedPatientName}
              queueEntry={selectedQueueEntry}
              onNewInvoice={openNewInvoice}
              onCollectPayment={setCollectInvoice}
              onBookAppointment={setBookPatient}
              onEditPatient={setEditPatient}
              onPrintPrescription={setPrintRx}
              onPrintInvoice={setPrintInvoice}
              onPriority={handlePriority}
              onRemoveFromQueue={handleRemoveFromQueue}
              onViewFullHistory={setDrawerPatientId}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center px-8 gap-3">
              <div className="w-14 h-14 rounded-2xl bg-muted/50 flex items-center justify-center mb-1">
                <Users className="w-6 h-6 text-muted-foreground/30" />
              </div>
              <div>
                <p className="text-sm font-semibold text-muted-foreground">No patient selected</p>
                <p className="text-xs text-muted-foreground/50 mt-1">
                  Click any patient in the queue to see details
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Patient history drawer ──────────────────────────────────────────── */}
      {drawerPatientId && (
        <PatientDrawer
          patientId={drawerPatientId}
          onClose={() => setDrawerPatientId(null)}
          onCollectPayment={inv => { setDrawerPatientId(null); setCollectInvoice(inv); }}
          onPrintPrescription={setPrintRx}
          onPrintInvoice={setPrintInvoice}
        />
      )}

      {/* ── Modals ──────────────────────────────────────────────────────────── */}

      {billingPatient && (
        <MedicalBillingModal
          isOpen
          onClose={() => setBillingPatient(null)}
          patient={billingPatient}
          services={billingServices}
          onCreateInvoice={handleCreateInvoice}
          loading={billingLoading}
          clinicId={clinicId}
          doctorId={
            selectedDoctorId !== 'all' ? selectedDoctorId : doctors[0]?.id ?? currentUser?.doctor_id ?? ''
          }
          doctors={doctors}
        />
      )}

      <PaymentRecordModal
        isOpen={!!collectInvoice}
        onClose={() => setCollectInvoice(null)}
        invoice={collectInvoice}
        onRecordPayment={handleCollectPayment}
        loading={false}
      />

      {editPatient && (
        <PatientFormModal
          isOpen
          onClose={() => setEditPatient(null)}
          onSuccess={async () => {
            setEditPatient(null);
            await loadAll();
          }}
          mode="edit"
          patientId={editPatient.id}
          initialData={{
            name: editPatient.name ?? '',
            age: editPatient.age?.toString() ?? '',
            gender: editPatient.gender ?? '',
            phone: editPatient.phone ?? '',
            phone_2: editPatient.phone_2 ?? '',
            email: editPatient.email ?? '',
            address: editPatient.address ?? '',
            city: editPatient.city ?? '',
            pin_code: editPatient.pin_code ?? '',
            blood_group: editPatient.blood_group ?? '',
            allergies: editPatient.allergies ?? '',
            date_of_birth: editPatient.date_of_birth ?? '',
            preferred_language: editPatient.preferred_language ?? 'English',
            aadhar_number: editPatient.aadhar_number ?? '',
            emergency_contact_name: editPatient.emergency_contact_name ?? '',
            emergency_contact_phone: editPatient.emergency_contact_phone ?? '',
            payment_preference: editPatient.payment_preference ?? 'Cash',
          }}
          showPrescriptionOption={false}
          showQueueOption={false}
          title="Edit Patient"
        />
      )}

      {bookPatient && (
        <AddAppointmentModal
          isOpen
          onClose={() => setBookPatient(null)}
          patientId={bookPatient.id}
          patients={bookPatient ? [bookPatient] : []}
          showPatientSelection={false}
          onAdded={() => {
            setBookPatient(null);
            toast({ title: 'Appointment booked' });
            loadAll();
          }}
        />
      )}

      <PatientFormModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSuccess={handleRegisterSuccess}
        title="Register New Patient"
        showPrescriptionOption={false}
        showQueueOption={false}
        initialData={prefillPhone ? { phone: prefillPhone } : undefined}
      />

      {printRx && (
        <PrintPrescriptionPreviewModal
          open={!!printRx}
          prescription={printRx}
          onClose={() => setPrintRx(null)}
        />
      )}

      {printInvoice && (
        <PrintBillPreviewModal
          open={!!printInvoice}
          invoice={printInvoice}
          onClose={() => setPrintInvoice(null)}
        />
      )}
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ label, dotClass, bgClass }: { label: string; dotClass: string; bgClass: string }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 border-y border-border/40 ${bgClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`} />
      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
    </div>
  );
}

// ─── Queue row ────────────────────────────────────────────────────────────────

interface QueueRowProps {
  entry: QueueEntryEx;
  selected: boolean;
  showDoctor: boolean;
  onClick: () => void;
  confirmingRemove: boolean;
  onRequestRemove: () => void;
  onConfirmRemove: () => void;
  onCancelRemove: () => void;
  dim?: boolean;
}

function QueueRow({
  entry, selected, showDoctor, onClick, dim,
  confirmingRemove, onRequestRemove, onConfirmRemove, onCancelRemove,
}: QueueRowProps) {
  const isConsult = entry.status === 'in_consultation';
  const isDone    = entry.status === 'completed';
  const isWaiting = entry.status === 'waiting';
  const isLong    = (entry.waitingTimeMinutes ?? 0) >= 30;
  const isMedium  = !isLong && (entry.waitingTimeMinutes ?? 0) >= 15;

  return (
    <div
      className={`group relative flex items-center gap-2.5 px-3 py-2.5 border-b border-border/40 transition-colors cursor-pointer ${
        selected
          ? 'bg-primary/8 border-l-[3px] border-l-primary'
          : 'hover:bg-muted/50 border-l-[3px] border-l-transparent'
      } ${dim ? 'opacity-55' : ''}`}
      onClick={confirmingRemove ? undefined : onClick}
    >
      {/* Token number */}
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 tabular-nums ${
        isConsult ? 'bg-primary text-primary-foreground' :
        isDone    ? 'bg-success/15 text-success' :
        isLong    ? 'bg-destructive/15 text-destructive' :
        isMedium  ? 'bg-warning/15 text-warning' :
                    'bg-muted text-muted-foreground'
      }`}>
        {entry.tokenNumber}
      </div>

      {confirmingRemove ? (
        <div className="flex-1 flex items-center justify-between gap-2" onClick={e => e.stopPropagation()}>
          <span className="text-xs text-destructive font-medium truncate">Remove {entry.patientName.split(' ')[0]}?</span>
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={onConfirmRemove}
              className="px-2 py-0.5 rounded text-[11px] font-bold bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
            >
              Yes
            </button>
            <button
              onClick={onCancelRemove}
              className="px-2 py-0.5 rounded text-[11px] font-bold bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
            >
              No
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold truncate ${isDone ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
              {entry.patientName}
            </p>
            <p className={`text-[11px] truncate mt-0.5 ${
              isConsult ? 'text-primary font-medium' :
              isDone    ? 'text-muted-foreground' :
                          waitColor(entry.waitingTimeMinutes)
            }`}>
              {isConsult
                ? `● Live${showDoctor ? ` · ${drName(entry.doctorName)}` : ''}`
                : isDone
                ? `Done${showDoctor ? ` · ${drName(entry.doctorName)}` : ''}`
                : [
                    entry.waitingTimeMinutes != null && `${entry.waitingTimeMinutes}m`,
                    showDoctor && drName(entry.doctorName),
                  ].filter(Boolean).join(' · ')
              }
            </p>
          </div>

          {/* Remove button — visible on hover for waiting entries */}
          {isWaiting && (
            <button
              onClick={e => { e.stopPropagation(); onRequestRemove(); }}
              className="opacity-0 group-hover:opacity-100 p-1 rounded text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-all flex-shrink-0"
              title="Remove from queue"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── Appointment row ──────────────────────────────────────────────────────────

interface ApptRowProps {
  apt: AppointmentRow;
  selected: boolean;
  checkingIn: boolean;
  showDoctor: boolean;
  doctors: Doctor[];
  onSelect: () => void;
  onCheckIn: () => void;
}

function ApptRow({ apt, selected, checkingIn, showDoctor, doctors, onSelect, onCheckIn }: ApptRowProps) {
  const docName = showDoctor && apt.doctor_id
    ? doctors.find(d => d.id === apt.doctor_id)?.name
    : null;

  return (
    <div
      className={`flex items-center gap-2.5 px-3 py-2.5 border-b border-border/40 transition-colors cursor-pointer ${
        selected
          ? 'bg-primary/8 border-l-[3px] border-l-primary'
          : 'hover:bg-muted/50 border-l-[3px] border-l-transparent'
      }`}
      onClick={onSelect}
    >
      {/* Time chip */}
      <div className="w-8 h-8 rounded-lg bg-muted flex flex-col items-center justify-center flex-shrink-0">
        <span className="text-[9px] font-bold text-muted-foreground leading-tight tabular-nums text-center whitespace-pre-line">
          {formatTime(apt.time).replace(' ', '\n')}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-foreground truncate">{apt.patientName}</p>
        <p className="text-[11px] text-muted-foreground truncate mt-0.5">
          {[apt.reason || 'Appointment', docName && drName(docName)].filter(Boolean).join(' · ')}
        </p>
      </div>

      <button
        disabled={checkingIn}
        onClick={e => { e.stopPropagation(); onCheckIn(); }}
        className="flex-shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-success/10 text-success hover:bg-success/20 disabled:opacity-50 transition-colors"
      >
        {checkingIn ? '…' : 'Check In'}
      </button>
    </div>
  );
}
